import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../../utils/apiClient';

const EMPTY_COUNTS = { chatId: null, media: 0, files: 0, links: 0 };

/**
 * Откуда берётся содержимое переписки — из личного чата или из группы.
 *
 * Разделы «Медиа / Файлы / Ссылки» одинаковы в профиле собеседника и на
 * странице группы, а адреса разные: личный чат сервер ищет по id второго
 * участника, групповой — по id самого чата. Поэтому источник описывается здесь,
 * а хуки принимают его целиком и про эту разницу больше не знают.
 *
 * @param {{userId?: number}|{groupChatId?: number}} source
 */
function resolveSource(source) {
    const groupId = Number(source?.groupChatId);
    if (source && 'groupChatId' in source) {
        return {
            valid: Number.isFinite(groupId) && groupId > 0,
            counts: `/api/chats/group/${groupId}/content-counts`,
            content: `/api/chats/group/${groupId}/content`,
            links: `/api/chats/group/${groupId}/links`,
            key: `group-${source.groupChatId}`,
        };
    }

    const userId = Number(source?.userId);
    return {
        valid: Number.isFinite(userId) && userId > 0,
        counts: `/api/messages/content-counts/${userId}`,
        content: `/api/messages/content/${userId}`,
        links: `/api/messages/links/${userId}`,
        key: `user-${source?.userId}`,
    };
}

/**
 * Сколько в переписке медиа, файлов и ссылок.
 *
 * Нужен профилю, чтобы решить, показывать ли разделы: пустые вкладки, в которые
 * нечего открыть, лучше не показывать вовсе.
 *
 * @param {{userId?: number}|{groupChatId?: number}} source
 * @param {boolean} [enabled] - спрашивать ли (гостю и без id — незачем)
 */
export function useChatContentCounts(source, enabled = true) {
    const [counts, setCounts] = useState(EMPTY_COUNTS);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const { valid, counts: countsPath, key } = resolveSource(source);

    useEffect(() => {
        if (!enabled || !valid) {
            setCounts(EMPTY_COUNTS);
            return;
        }

        let cancelled = false;
        setLoading(true);

        (async () => {
            try {
                const res = await apiFetch(countsPath, { method: 'GET' });
                const data = await res.json();

                if (!res.ok) throw new Error(data?.error ?? 'Ошибка сервера');
                if (cancelled) return;

                setCounts({
                    chatId: data?.chatId ?? null,
                    media: Number(data?.media) || 0,
                    files: Number(data?.files) || 0,
                    links: Number(data?.links) || 0,
                });
                setError(null);
            } catch (err) {
                if (!cancelled) {
                    setCounts(EMPTY_COUNTS);
                    setError(err.message);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => { cancelled = true; };
        // key вместо самого объекта: источник приходит новым литералом на
        // каждый рендер, и по ссылке эффект перезапускался бы вечно.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key, enabled, valid]);

    return {
        counts,
        // Вкладки показываем, только когда есть и переписка, и что показывать.
        hasContent: counts.chatId !== null
            && (counts.media > 0 || counts.files > 0 || counts.links > 0),
        loading,
        error,
    };
}

/**
 * Содержимое переписки одной категории, новые сверху.
 *
 * Курсор — id последнего разобранного СООБЩЕНИЯ, а не вложения: в одном
 * сообщении бывает до пяти файлов, поэтому длина списка с числом сообщений не
 * совпадает.
 *
 * @param {{userId?: number}|{groupChatId?: number}} source
 * @param {'media'|'files'|'links'} type
 * @param {boolean} [enabled] - вкладка открыта
 */
export function useChatContent(source, type, enabled = true) {
    const [items, setItems] = useState([]);
    const [cursor, setCursor] = useState(null);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);

    const { valid, content: contentPath, links: linksPath, key } = resolveSource(source);
    const active = enabled && valid && Boolean(type);

    const fetchPage = useCallback(async (from) => {
        const path = type === 'links' ? linksPath : contentPath;

        const url = new URL(path, process.env.REACT_APP_API_URL);
        if (type !== 'links') url.searchParams.append('type', type);
        if (from) url.searchParams.append('cursor', String(from));

        const res = await apiFetch(url.toString(), { method: 'GET' });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error ?? 'Ошибка сервера');

        return data;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key, type]);

    useEffect(() => {
        if (!active) {
            setItems([]);
            setCursor(null);
            setHasMore(false);
            return;
        }

        let cancelled = false;
        setLoading(true);

        fetchPage(null)
            .then(data => {
                if (cancelled) return;
                setItems(data?.items ?? []);
                setCursor(data?.nextCursor ?? null);
                setHasMore(Boolean(data?.nextCursor));
                setError(null);
            })
            .catch(err => {
                if (!cancelled) setError(err.message);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => { cancelled = true; };
    }, [active, fetchPage]);

    const loadMore = useCallback(async () => {
        if (!active || !hasMore || loading || loadingMore) return;

        setLoadingMore(true);
        try {
            const data = await fetchPage(cursor);
            setItems(prev => [...prev, ...(data?.items ?? [])]);
            setCursor(data?.nextCursor ?? null);
            setHasMore(Boolean(data?.nextCursor));
        } catch (err) {
            setError(err.message);
            setHasMore(false);
        } finally {
            setLoadingMore(false);
        }
    }, [active, hasMore, loading, loadingMore, cursor, fetchPage]);

    return { items, loading, loadingMore, hasMore, loadMore, error };
}
